import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { searchCustomersText, getMajorCompanies } from "actions/customer";
import { getAllSurveyTypes } from "actions/user";
import { MSpinner } from "components/Shared";
import MSelect from "components/Shared/MSelect.tsx";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { CustomerListModel, SurveyTypeListDto, MajorCompanyDto, EmployeeCustomerSurveyTypeDto } from "interfaces/Mueller.Business.Models";
import { fieldAppointmentQueueTypeMSelectOption, LabelType, MSelectOptionNumberValue, MSelectOptionStringValue, preferredAppointmentQueueTypeMSelectOption } from "typeConstants/employeeCustomerSurveyTypes";

interface Props {
  onCancel: () => void,
  onSubmit: (mappings: EmployeeCustomerSurveyTypeDto[]) => void, 
  overrideQueueTypes?: QueueType[],
  // Redux
  customerList: CustomerListModel[],
  fetchingCustomerList: boolean,
  getCustomers: () => void,
  allSurveyTypes: SurveyTypeListDto[],
  fetchingAllSurveyTypesList: boolean,
  getAllSurveyTypes: () => void,
  getMajorCompanies: () => void;
  majorCompanies: MajorCompanyDto[];
}

export type QueueType = MSelectOptionStringValue;

export const fieldAppointmentQueueType = {
  label: "Field Appointment",
  value: "Field Appointment",
};

export const preferredAppointmentQueueType = {
  label: "Preferred Appointment",
  value: "Preferred Appointment",
};

export const auditQAQueueType = {
  label: "Audit QA",
  value: "Audit QA",
};

export const homeownersQAQueueType = {
  label: "Homeowners QA",
  value: "Homeowners QA",
}

const defaultQueueTypes = [fieldAppointmentQueueTypeMSelectOption, preferredAppointmentQueueTypeMSelectOption];

const AddMapping = (props: Props)=> {
  const [selectedQueue, setSelectedQueue] = useState<MSelectOptionStringValue | null>();
  const [selectedMajorCompany, setSelectedMajorCompany] = useState<MSelectOptionStringValue | null>();
  const [selectedCustomer, setSelectedCustomer] = useState<MSelectOptionNumberValue | null>();
  const [selectedSurveyTypes, setSelectedSurveyTypes] = useState<MSelectOptionStringValue[] | null>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isExcluded, setIsExcluded] = useState<boolean>(false);
  const mounted = useRef(true);

  useEffect(() => {
    if (props.overrideQueueTypes?.length === 1) setSelectedQueue(props.overrideQueueTypes[0]);

    if (props.customerList.length == 0 && !props.fetchingCustomerList) {
      props.getCustomers();
    }
    if (props.allSurveyTypes.length == 0 && !props.fetchingAllSurveyTypesList) {
      props.getAllSurveyTypes();
    }
    if (!props.majorCompanies) {
      props.getMajorCompanies();
    }
    return () => {
      mounted.current = false;
    };
  }, []);
  
  const onSelectQueue = (newValue: MSelectOptionStringValue) => setSelectedQueue(newValue);
  const onSelectMajorCompany  = (newValue: MSelectOptionStringValue) => {
    setSelectedMajorCompany(newValue);
    setSelectedCustomer(null);
    setSelectedSurveyTypes(null);
  }
  const onSelectCustomer = (newValue: MSelectOptionNumberValue) => {
    setSelectedCustomer(newValue);
    setSelectedSurveyTypes(null);
  };
  const onSelectSurveyType = (newValue: MSelectOptionStringValue[]) => setSelectedSurveyTypes(newValue);

  const filteredCustomers = selectedMajorCompany 
    ? props.customerList.filter(c => c.majorCompany.code === selectedMajorCompany.value)
    : props.customerList;
  const customerOptions = filteredCustomers.map(c => ({ label: `${c.number} ${c.displayName}`, value: c.number}));

  const filteredSurveyTypeCodes = selectedCustomer
    ?  props.customerList.find(c => c.number === selectedCustomer.value)?.activeSurveyTypeCodes
    : filteredCustomers.reduce((acc, cur) => acc.concat(cur.activeSurveyTypeCodes || []), []);
  const surveyTypeOptions = props.allSurveyTypes.filter(s => filteredSurveyTypeCodes.includes(s.code))
    .map(s => ({ label: s.name, value: s.code}));

  const getLabel = () => {
    const getSurveyTypeLabel = () => {
      if (Array.isArray(selectedSurveyTypes)) {
        const surveyTypeLabels = selectedSurveyTypes.map(s => s.label);
        return surveyTypeLabels.join(", ");
      }
    };
    const getCustomerAccountLabel = () => {
      if (selectedCustomer) {
        return `${selectedCustomer.label}`;
      } else if (selectedMajorCompany) {
        const companyAccounts = props.customerList.filter(c => c.majorCompany.code === selectedMajorCompany.value);
        const accountLabels = companyAccounts.map(c => (`${c.displayName}`));
        return accountLabels.join(", ");
      }
    };

    const labelTypeMapping = {
      [LabelType.QUEUE]: `all cases in ${selectedQueue?.label} queue`,
      [LabelType.MAJOR_COMPANY]: `all cases in accounts under major company ${selectedMajorCompany?.label} in ${selectedQueue?.label} queue`,
      [LabelType.CUSTOMER_ACCOUNT]: `all cases under account ${getCustomerAccountLabel()} in ${selectedQueue?.label} queue`,
      [LabelType.SURVEY_TYPES]: `${getSurveyTypeLabel()} cases in ${selectedQueue?.label} queue`,
      [LabelType.COMPANY_SURVEY_TYPES]: `${getSurveyTypeLabel()} cases in all accounts under major company ${selectedMajorCompany?.label} in ${selectedQueue?.label} queue`,
      [LabelType.CUSTOMER_ACCOUNT_SURVEY_TYPES]: `${getSurveyTypeLabel()} cases under account ${getCustomerAccountLabel()} in ${selectedQueue?.label} queue`,
      [LabelType.EMPTY]: "",
    };

    const getLabelType = () => {
      const hasSurveyType = (Array.isArray(selectedSurveyTypes) && selectedSurveyTypes.length > 0);
      if (selectedQueue && !hasSurveyType && !selectedCustomer && !selectedMajorCompany) return LabelType.QUEUE;
      if (selectedQueue && !hasSurveyType && !selectedCustomer && selectedMajorCompany) return LabelType.MAJOR_COMPANY;
      if (selectedQueue && hasSurveyType && !selectedCustomer && !selectedMajorCompany) return LabelType.SURVEY_TYPES;
      if (selectedQueue && !hasSurveyType && selectedCustomer) return LabelType.CUSTOMER_ACCOUNT;
      if (selectedQueue && hasSurveyType && !selectedCustomer && selectedMajorCompany) return LabelType.COMPANY_SURVEY_TYPES;
      if (selectedQueue && hasSurveyType && selectedCustomer) return LabelType.CUSTOMER_ACCOUNT_SURVEY_TYPES;
      return LabelType.EMPTY;
    };

    let mappingCount = 1;
    if (selectedMajorCompany && !selectedCustomer) {
      mappingCount = filteredCustomers.length;
    }
    if (Array.isArray(selectedSurveyTypes) && selectedSurveyTypes.length > 0) {
      // Add a mapping for each account survey type that's active for the customers selected
      if (selectedMajorCompany && !selectedCustomer) {
        let surveyTypeCount = 0;
        filteredCustomers.forEach(c => {
          selectedSurveyTypes.forEach(s => {
            if (c.activeSurveyTypeCodes.includes(s.value)) {
              surveyTypeCount++;
            }
          });
        });
        mappingCount = surveyTypeCount;
      } else if (selectedCustomer) {
        // If only one customer is selected, we should only have their active survey types
        mappingCount = selectedSurveyTypes.length;
      }
    }
    const mappingOrExclusion = isExcluded ? "exclusion" : "mapping";
    const labelStart = `Add ${mappingCount > 1 ? `${mappingCount} ` : ""}${mappingOrExclusion}${mappingCount > 1 ? "s" : ""} for `;

    return labelStart + labelTypeMapping[getLabelType()];
  };

  const onSubmit = () => {
    setSubmitting(true);
    let mappings: EmployeeCustomerSurveyTypeDto[] = [];

    // Queue only
    if (selectedQueue && !(Array.isArray(selectedSurveyTypes) && selectedSurveyTypes.length > 0) && !selectedCustomer && !selectedMajorCompany) {
      mappings.push({
        employeeId: null,  // Added in backend
        queueTypeCode: selectedQueue.value,
        customerNumber: null,
        customerName: null,
        surveyType: null,
        surveyTypeCode: null,
        isExcluded: isExcluded,
      });
      props.onSubmit(mappings);
      return;
    }

    // Survey types only
    if (selectedQueue && (Array.isArray(selectedSurveyTypes) && selectedSurveyTypes.length > 0) && !selectedCustomer && !selectedMajorCompany) {
      selectedSurveyTypes.forEach(s => {
        mappings.push({
          employeeId: null,  // Added in backend
          queueTypeCode: selectedQueue.value,
          customerNumber: null,
          customerName: null,
          surveyType: null, // Not used for adding
          surveyTypeCode: s.value,
          isExcluded: isExcluded,
        });
      });
      props.onSubmit(mappings);
      return;
    }
    
    // Major company and customers
    let customers: CustomerListModel[] = []; 
    if (selectedMajorCompany && !selectedCustomer) {
      customers = filteredCustomers;
    } else {
      const customer = props.customerList.find(c => c.number === selectedCustomer.value);
      customers.push(customer);
    }
    customers.forEach(c => {
      mappings.push({
        employeeId: null,  // Added in backend
        queueTypeCode: selectedQueue.value,
        customerNumber: c.number,
        customerName: null, // Not used for adding 
        surveyType: null, // Not used for adding
        surveyTypeCode: null, // Added below if needed
        isExcluded: isExcluded,
      });
    });

    // Customers without survey types
    if (!(Array.isArray(selectedSurveyTypes) && selectedSurveyTypes.length > 0)) {
      props.onSubmit(mappings);
      return;
    }

    // Customers with survey types
    if (Array.isArray(selectedSurveyTypes) && selectedSurveyTypes.length > 0) {
      // Start over since we need to add mappings for each customer survey type
      mappings = [];
      customers.forEach(c => {
        selectedSurveyTypes.forEach(s => {
          // Only add a mapping if the customer has the survey type setup and active
          if (c.activeSurveyTypeCodes.includes(s.value)) {
            mappings.push({
              employeeId: null,  // Added in backend
              queueTypeCode: selectedQueue.value,
              customerNumber: c.number,
              customerName: null, // Not used for adding mapping
              surveyType: null, // Not used for adding mapping
              surveyTypeCode: s.value,
              isExcluded: isExcluded,
            });
          }
        });
      });
    } 

    props.onSubmit(mappings);
  };

  if (props.fetchingCustomerList || props.fetchingAllSurveyTypesList || submitting) {
    return (<MSpinner />);
  }

  return (
    <div className="add-employee-mapping">
      <MSelect
        className="add-employee-mapping-queue"
        key="add-employee-mapping-queue"
        label="Select Queue"
        placeholder="Select Queue"
        isClearable={false}
        onChange={onSelectQueue}
        options={props.overrideQueueTypes ?? defaultQueueTypes}
        value={selectedQueue}
        enableAllOption={false}
        menuPlacement="bottom"
      />
      <MSelect
        className="add-employee-mapping-major-company" 
        key="add-employee-mapping-major-company"
        label="Select Major Company (optional)"
        placeholder="Select Major Company (optional)"
        onChange={onSelectMajorCompany}
        options={props.majorCompanies?.map(c => ({ label: `${c.companyName}`, value: c.code}))}
        value={selectedMajorCompany}
        enableAllOption={false}
        menuPlacement="bottom"
      />
      <MSelect
        className="add-employee-mapping-customer" 
        key="add-employee-mapping-customer"
        label="Select Customer (optional)"
        placeholder="Select Customer (optional)"
        onChange={onSelectCustomer}
        options={customerOptions}
        value={selectedCustomer}
        enableAllOption={false}
        menuPlacement="bottom"
      />
      <MSelect
        className="add-employee-mapping-survey-type"
        key="add-employee-mapping-survey-type"
        label="Select Survey Type (optional)"
        placeholder="Select Survey Type (optional)"
        onChange={onSelectSurveyType}
        options={surveyTypeOptions}
        value={selectedSurveyTypes}
        isMulti
        enableAllOption
        menuPlacement="bottom"
      />
      <FormControlLabel
        control={
          <Checkbox
            name="add-employee-is-excluded"
            id="add-employee-is-excluded"
            className="add-employee-is-excluded"
            onChange={(e) => setIsExcluded(e.target.checked)}
            checked={isExcluded}
          />
        }
        label="Is Excluded"
      />
      <p>{getLabel()}</p>
      <div className="flex-row-between">
      <Button
        onClick={onSubmit}
        disabled={!selectedQueue}
      >
        Submit
      </Button>
      <Button
        onClick={props.onCancel}
      >
        Cancel
      </Button>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  customerList: state.customer.customerList,
  fetchingCustomerList: state.customer.fetchingCustomerList,
  allSurveyTypes: state.user.allSurveyTypes,
  fetchingAllSurveyTypesList: state.user.isFetchingAllSurveyTypesList,
  majorCompanies: state.customer.majorCompanies,
});

const mapDispatchToProps = dispatch => ({
  getCustomers() {
    dispatch(searchCustomersText());  // Searching without a search term will get all customers
  },
  getAllSurveyTypes() {
    dispatch(getAllSurveyTypes());
  },
  getMajorCompanies() {
    dispatch(getMajorCompanies());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AddMapping);